import React from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import { ClassNameMap } from '@mui/styles/withStyles';
import moment from 'moment';

import { Box, Button, Grid, Typography } from '@mui/material';

import { ReceiverUser, SenderUser, UnknownObject, User } from '../../../../../../../react-app-env';
import ReadOnlyBlock from '../../../../../../../components/ReadOnlyBlock';
import SpecialityCard from '../../../../../../../components/SpecialityCard';
import PotQR from '../../../../../../../components/QrCodes/PotQR';

import { Case, getPatientInfoFromEvents } from '../../case';

import styles from './styles';
import { Download } from '@mui/icons-material';
import { Document } from '../../../../../../../utils/types';

interface Props extends WithStyles<typeof styles> {
  classes: ClassNameMap<string>;
  caseData: Case | UnknownObject;
  user: User | ReceiverUser | SenderUser | null;
}

const StepCase = ({ classes, caseData, user }: Props): React.ReactElement => {
  const patientName = getPatientInfoFromEvents(caseData);
  const patientNhsNumber = caseData?.patient?.nhs_id || '';
  const patientDob = moment(caseData?.patient?.dob).format('DD/MM/yyyy');

  const downloadFile = (fileId: string) => {
    window.open(`${process.env.REACT_APP_AWS_PDF_REPORTS_URL}/${fileId}`, '_blank');
  };

  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Grid container spacing={4}>
            <Grid item xs={4}>
              <ReadOnlyBlock
                fill
                title="Patient Details"
                items={[
                  {
                    label: 'Name',
                    value: patientName,
                  },
                  { label: 'NHS number', value: patientNhsNumber },
                  { label: 'Medical Record Number', value: caseData.mrn },
                  { label: 'Date of Birth', value: patientDob },
                ]}
                components={[
                  <>
                    {!!caseData?.documents?.length && (
                      <Box>
                        <Typography variant="subtitle1" color="textSecondary">
                          Document(s)
                        </Typography>
                        {caseData?.documents?.map((doc: Document) => (
                          <Button
                            key={doc.id}
                            variant="text"
                            sx={{ textTransform: 'none' }}
                            color="primary"
                            startIcon={<Download />}
                            onClick={() => downloadFile(`${doc.id}/${encodeURIComponent(doc.document_name)}`)}>
                            {doc.document_name}
                          </Button>
                        ))}
                      </Box>
                    )}
                  </>,
                ]}
              />
            </Grid>
            <Grid item xs={4}>
              <ReadOnlyBlock
                fill
                title="Sender Details"
                items={[
                  { label: 'Organisation', value: caseData.organisation?.name },
                  { label: 'Lab', value: caseData.lab?.name },
                ]}
              />
            </Grid>
            <Grid item xs={4}>
              <ReadOnlyBlock
                fill
                center
                components={[
                  <PotQR
                    patientInformation={{
                      name: patientName,
                      nhsNumber: patientNhsNumber,
                      dob: patientDob,
                    }}
                    rawData={caseData.id}
                  />,
                ]}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={4}>
            <Grid item xs={6}>
              <ReadOnlyBlock
                fill
                title="Clinical Details"
                components={(caseData as Case).surgical_pathways.map((pathway) => (
                  <div className={classes.reviewDetailsContainer}>
                    <SpecialityCard small divider gridSize={12} title={pathway.category.speciality.name} description={pathway.category.name} />
                    <div className={classes.specialityDetails}>
                      <Grid container spacing={2}>
                        {pathway.surgical_pathways?.map((pathway: UnknownObject) => (
                          <Grid key={pathway.id} item xs={6}>
                            <div className={classes.specialityStep}>
                              <Typography variant="subtitle2" color="textSecondary">
                                {pathway.title}
                              </Typography>
                              <Typography className={classes.stepValue} variant="body1">
                                {pathway.label}
                              </Typography>
                            </div>
                          </Grid>
                        ))}
                        {pathway.note && (
                          <Grid item xs={12}>
                            <Typography variant="body1" color="textSecondary">
                              Additional information: {pathway.note}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </div>
                  </div>
                ))}
              />
            </Grid>
            <Grid item xs={6}>
              <ReadOnlyBlock
                fill
                title="Samples"
                components={[
                  <div className={classes.reviewDetailsContainer}>
                    {(caseData as Case).surgical_pathways?.map((version) => {
                      const pots = (caseData as Case).pots.filter((i) => i.speciality_category_id === version.category.id);

                      return (
                        <Box>
                          <SpecialityCard small divider gridSize={12} title={version.category.speciality.name} description={version.category.name} />
                          {pots.map((pot) => (
                            <div key={pot.id} className={classes.specialityDetails}>
                              <Typography variant="subtitle1" color="textSecondary">
                                {pot.name}
                              </Typography>
                              <Typography className={classes.stepValue} variant="body1">
                                {pot.pathway.map((i: UnknownObject) => i.label).join(', ')}
                              </Typography>
                              {pot.note && (
                                <Typography className={classes.stepValue} variant="subtitle2" color="textSecondary">
                                  Additional information: {pot.note}
                                </Typography>
                              )}
                            </div>
                          ))}
                        </Box>
                      );
                    })}
                  </div>,
                ]}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default withStyles(styles)(StepCase);
